import React, { useState, useEffect } from 'react';
import { adminService } from '../services/adminService.js';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';

function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: '' });
  const [hovered, setHovered] = useState({});
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, totalPages: 0 });
  const [filters, setFilters] = useState({ minScore: '', maxScore: '', page: 1 });
  const [showStats, setShowStats] = useState(true);

  useEffect(() => {
    loadData();
  }, [filters.page, filters.minScore, filters.maxScore]);

  const loadData = async () => {
    try {
      setLoading(true);
      setStatsLoading(true);
      const params = { page: filters.page, limit: 20 };
      if (filters.minScore) params.minScore = filters.minScore;
      if (filters.maxScore) params.maxScore = filters.maxScore;

      const [reviewsResult, statsResult] = await Promise.all([
        adminService.getReviews(params),
        adminService.getReviewsStats(),
      ]);
      setReviews(reviewsResult.reviews || []);
      setPagination(reviewsResult.pagination || { total: 0, page: 1, limit: 20, totalPages: 0 });
      setStats(statsResult);
    } catch (err) {
      toast.error('Erro ao carregar avaliações');
    } finally {
      setLoading(false);
      setStatsLoading(false);
    }
  };

  const renderStars = (score) => {
    return Array.from({ length: 5 }, (_, i) => (
      <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill={i < score ? '#F59E0B' : '#E5E7EB'} stroke={i < score ? '#F59E0B' : '#E5E7EB'} strokeWidth="1">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
      </svg>
    ));
  };

  return (
    <div className="admin-page">
      {showStats && (
        statsLoading ? (
          <div className="stats-grid stats-grid-2">
            {[1,2].map(i => <div key={i} className="shimmer shimmer-card"></div>)}
          </div>
        ) : (
          stats && (
            <div className="stats-grid stats-grid-2">
              <div className="stat-card">
                <div className="stat-card-content">
                  <div className="stat-card-info">
                    <span className="stat-label">Total de Avaliações</span>
                    <span className="stat-value">{stats.totalReviews?.toLocaleString('pt-BR') || '0'}</span>
                  </div>
                  <div className="stat-icon" style={{ background: '#4F46E515' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                  </div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-card-content">
                  <div className="stat-card-info">
                    <span className="stat-label">Nota Média</span>
                    <span className="stat-value">{stats.avgScore?.toFixed(1) || '-'}</span>
                  </div>
                  <div className="stat-icon" style={{ background: '#F59E0B15' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="#F59E0B" stroke="#F59E0B" strokeWidth="0"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                  </div>
                </div>
              </div>
            </div>
          )
        )
      )}

      {/* Filters */}
      <div className="admin-filters">
        <div className="filter-group">
          <select value={filters.minScore} onChange={(e) => setFilters(f => ({ ...f, minScore: e.target.value, page: 1 }))} className="filter-control">
            <option value="">Nota mínima</option>
            <option value="1">1 estrela+</option>
            <option value="2">2 estrelas+</option>
            <option value="3">3 estrelas+</option>
            <option value="4">4 estrelas+</option>
            <option value="5">5 estrelas</option>
          </select>
          <select value={filters.maxScore} onChange={(e) => setFilters(f => ({ ...f, maxScore: e.target.value, page: 1 }))} className="filter-control">
            <option value="">Nota máxima</option>
            <option value="1">Até 1 estrela</option>
            <option value="2">Até 2 estrelas</option>
            <option value="3">Até 3 estrelas</option>
            <option value="4">Até 4 estrelas</option>
            <option value="5">Até 5 estrelas</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="admin-table-card">
        <div className="table-header">
          <h3>Avaliações ({pagination.total})</h3>
        </div>
        {loading ? (
          <div className="table-loading">
            {[1,2,3,4,5].map(i => <div key={i} className="shimmer shimmer-row"></div>)}
          </div>
        ) : (
          <div className="table-responsive">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Usuário</th>
                  <th>Negócio</th>
                  <th>Nota</th>
                  <th>Comentário</th>
                  <th>Data</th>
                </tr>
              </thead>
              <tbody>
                {reviews.length === 0 ? (
                  <tr><td colSpan="5" className="empty-table">Nenhuma avaliação encontrada</td></tr>
                ) : reviews.map((review, i) => (
                  <tr key={review.id || i}>
                    <td className="td-name">{review.user?.name || review.User?.name || review.userName || '-'}</td>
                    <td>{review.entity?.name || review.Entity?.name || review.entityName || '-'}</td>
                    <td><div className="stars-display">{renderStars(review.score)}</div></td>
                    <td className="td-comment">{review.content || review.comment || '-'}</td>
                    <td>{review.createdAt ? dayjs(review.createdAt).format('DD/MM/YYYY') : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {pagination.totalPages > 1 && (
          <div className="table-pagination">
            <button className="btn btn-sm" disabled={filters.page === 1} onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}>Anterior</button>
            <span className="pagination-info">Página {pagination.page} de {pagination.totalPages}</span>
            <button className="btn btn-sm" disabled={filters.page >= pagination.totalPages} onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}>Próxima</button>
          </div>
        )}
      </div>

      {tooltip.visible && (
        <div className="cursor-tooltip" style={{ left: tooltip.x + 'px', top: tooltip.y + 'px' }}>{tooltip.content}</div>
      )}

      {/* Top/Worst entities */}
      {/* {stats && (
        <div className="charts-grid">
          {stats.topEntities && stats.topEntities.length > 0 && (
            <div className="chart-card">
              <div className="chart-header"><h3>Melhores Avaliadas</h3></div>
              <div className="chart-body">
                <div className="ranking-list">
                  {stats.topEntities.map((entity, i) => (
                    <div key={i} className="ranking-item" onMouseEnter={(e) => { setHovered(h => ({ ...h, topEntities: i })); setTooltip({ visible: true, x: e.clientX + 12, y: e.clientY + 12, content: `${(entity.avgScore || entity.avgRating)?.toFixed(1) || '-'} (${entity.reviewsCount || ''})` }); }} onMouseMove={(e) => setTooltip(t => ({ ...t, x: e.clientX + 12, y: e.clientY + 12 }))} onMouseLeave={() => { setHovered(h => ({ ...h, topEntities: null })); setTooltip({ visible: false, x: 0, y: 0, content: '' }); }} style={{ opacity: (hovered.topEntities == null) || hovered.topEntities === i ? 1 : 0.28 }}>
                      <span className="ranking-pos">{i + 1}.</span>
                      <span className="ranking-name">{entity.name}</span>
                      <div className="stars-display">{renderStars(Math.round(entity.avgScore || entity.avgRating || 0))}</div>
                      <span className="ranking-score">{(entity.avgScore || entity.avgRating)?.toFixed(1)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {stats.worstEntities && stats.worstEntities.length > 0 && (
            <div className="chart-card">
              <div className="chart-header"><h3>Piores Avaliadas</h3></div>
              <div className="chart-body">
                <div className="ranking-list">
                  {stats.worstEntities.map((entity, i) => (
                    <div key={i} className="ranking-item">
                      <span className="ranking-pos">{i + 1}.</span>
                      <span className="ranking-name">{entity.name}</span>
                      <div className="stars-display">{renderStars(Math.round(entity.avgScore || entity.avgRating || 0))}</div>
                      <span className="ranking-score">{(entity.avgScore || entity.avgRating)?.toFixed(1)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )} */}
    </div>
  );
}

export default Reviews;